export interface ITemplatePart {
    type: TemplatePart;
    text: (ITemplatePart | string)[];
    link?: {
        url: string;
        target: string;
    };
}

export enum TemplatePart {
    Text = 1,
    Bold = 2,
    Italic = 3,
    Link = 4
}

export class TemplateParts {
    //#region SPLITING STRING TO PARTS

    private static separateValueToParts(value: string): ITemplatePart[] {
        const valueParts: ITemplatePart[] = [];
        let tmpText = '';
        for (let i = 0; i < value.length; i++) {
            const valuePart = this.getBoldedPart(value, i) || this.getItalicPart(value, i) || this.getLinkPart(value, i);
            if (valuePart) {
                this.getTextPart(tmpText, valueParts);
                tmpText = '';
                valueParts.push(valuePart.part);
                i = valuePart.current;
            } else {
                tmpText += value[i];
            }
        }
        this.getTextPart(tmpText, valueParts);
        return valueParts;
    }

    private static getTextPart(tmpText: string, parts: ITemplatePart[]): void {
        if (tmpText.length) {
            parts.push({
                type: TemplatePart.Text,
                text: [tmpText]
            });
        }
    }

    private static getBoldedPart(value: string, i: number): { part: ITemplatePart; current: number } | false {
        if (value[i] === '*' && value[i + 1] === '*') {
            const boldPart: ITemplatePart = {
                type: TemplatePart.Bold,
                text: []
            };
            let tmpText = '';
            for (let j = i + 2; j < value.length; j++) {
                if (value[j] === '*' && value[j + 1] === '*') {
                    if (tmpText.length) {
                        boldPart.text.push(tmpText);
                    }
                    return {
                        part: boldPart,
                        current: j + 1
                    };
                }

                const valuePart = this.getItalicPart(value, j) || this.getLinkPart(value, j);
                if (valuePart) {
                    if (tmpText.length) {
                        boldPart.text.push(tmpText);
                        tmpText = '';
                    }
                    boldPart.text.push(valuePart.part);
                    j = valuePart.current;
                } else {
                    tmpText += value[j];
                }
            }
            return false;
        } else {
            return false;
        }
    }

    private static getItalicPart(value: string, i: number): { part: ITemplatePart; current: number } | false {
        if (value[i] === '_') {
            const italicPart: ITemplatePart = {
                type: TemplatePart.Italic,
                text: []
            };
            let tmpText = '';
            for (let j = i + 1; j < value.length; j++) {
                if (value[j] === '_') {
                    if (tmpText.length) {
                        italicPart.text.push(tmpText);
                    }
                    return {
                        part: italicPart,
                        current: j
                    };
                }
                const valuePart = this.getBoldedPart(value, j) || this.getLinkPart(value, j);
                if (valuePart) {
                    if (tmpText.length) {
                        italicPart.text.push(tmpText);
                        tmpText = '';
                    }
                    italicPart.text.push(valuePart.part);
                    j = valuePart.current;
                } else {
                    tmpText += value[j];
                }
            }
            return false;
        } else {
            return false;
        }
    }

    private static getLinkPart(value: string, i: number): { part: ITemplatePart; current: number } | false {
        if (value[i] === '[') {
            const linkPart: ITemplatePart = {
                type: TemplatePart.Link,
                text: [],
                link: {
                    url: '',
                    target: 'target="_blank"'
                }
            };
            let tmpText = '';
            for (let j = i + 1; j < value.length; j++) {
                if (value[j] === ']') {
                    linkPart.text.push(tmpText);
                }
                if (value[j] === '(') {
                    tmpText = '';
                    for (let k = j + 1; k < value.length; k++) {
                        if (value[k] === ')') {
                            if (!linkPart.link!.url) {
                                linkPart.link!.url = tmpText;
                            } else {
                                linkPart.link!.target = tmpText;
                            }
                            return {
                                part: linkPart,
                                current: k
                            };
                        }
                        if (value[k] === ',') {
                            linkPart.link!.url = tmpText;
                            tmpText = '';
                            continue;
                        }
                        tmpText += value[k];
                    }
                }
                tmpText += value[j];
            }
            return false;
        } else {
            return false;
        }
    }

    //#endregion

    //#region ASSEBLING TEMPLATE

    private static getTextTemplate(text: string): string {
        return `<span>${text}</span>`;
    }

    private static getLinkTemplate(url: string, target: string, text: string): string {
        return `<a class="announcement-link" href="${url}" ${target}>${text}</a>`;
    }

    private static getBeginingSpanTag(classes?: string): string {
        classes = classes || '';
        return `<span class="${classes}">`;
    }

    private static getClosingSpanTag(): string {
        return '</span>';
    }

    private static getBeginingDivTag(classes?: string): string {
        classes = classes || '';
        return `<div class="${classes}">`;
    }

    private static getEndingDivTag(): string {
        return '</div>';
    }

    public static getTemplate(value: string): string {
        const valueParts = this.separateValueToParts(value);
        let template = this.getBeginingDivTag();
        for (let i = 0; i < valueParts.length; i++) {
            const part = valueParts[i];
            if (TemplatePart.Bold === part.type || TemplatePart.Italic === part.type) {
                if (part.type === TemplatePart.Bold) {
                    template += this.getBeginingSpanTag('bold');
                }
                if (part.type === TemplatePart.Italic) {
                    template += this.getBeginingSpanTag('italic');
                }
                for (let j = 0; j < part.text.length; j++) {
                    const subPart = part.text[j];
                    if (typeof subPart === 'string') {
                        template += this.getTextTemplate(subPart);
                    } else {
                        if (subPart.type === TemplatePart.Link) {
                            const url = subPart.link?.url ?? '';
                            const target = subPart.link?.target ?? '';
                            const text = (subPart.text[0] as string) ?? '';
                            template += this.getLinkTemplate(url, target, text);
                        }
                    }
                }
                template += this.getClosingSpanTag();
            }
            if (TemplatePart.Link === part.type) {
                const url = part.link?.url ?? '';
                const target = part.link?.target ?? '';
                const text = (part.text[0] as string) ?? '';
                template += this.getLinkTemplate(url, target, text);
            }
            if (TemplatePart.Text === part.type) {
                const text = (part.text[0] as string) ?? '';
                template += this.getTextTemplate(text);
            }
        }
        template += this.getEndingDivTag();

        return template;
    }

    //#endregion
}
